import React, { useState, useEffect, useRef } from 'react';
import MainRadar from '../Radar/MainRadar';
import RadarActionManager from 'utils/radarActionManager';
import { IndicatorsList } from 'pages/Project/Indicators/IndicatorsList';
import { defaults as IndicatorModelDefaults } from 'model/IndicatorModel';
import { defaults as StakeholderModelDefaults } from 'model/StakeholderModel';
import { defaults as RiskModelDefaults } from 'model/RiskModel';
import { ScenarioNavigation } from './ScenarioNavigation';
import { ScreenNavigation } from './ScreenNavigation';

import { useTranslation } from 'react-i18next';
import { RiskScatterFullChart } from 'components/charts/RiskScatterFullChart';
import { getItemAlteredByScenario } from 'utils/rules';
import { SynthesisGraph } from 'pages/Project/Navigator/SummaryColumn/SynthesisGraph';

import './VisualDisplay.sass';
interface VisualDisplayProps {
    width?: number;
    height?: number;
    initialState?: Record<string, any>;
    debug?: boolean;
    className?: string;
    isVisible?: Boolean;
    isNavigationEnabled?: Boolean;
}

/**
 * Composant d'affichage du radar qui réagit aux actions
 */
export const VisualDisplay: React.FC<VisualDisplayProps> = ({
    width = 900,
    height = 600,
    initialState,
    debug = false,
    className = '',
    isVisible = true
}) => {
    const [state, setState] = useState<Record<string, any>>(initialState || {
        canvas: { animated: false },
        stakeholders: {},
        indicators: {},
        risks: {},
        scenarios: {},
        scenario: null,
        currentScreen: null,
        imageUrl: null,
        recentCreations: {},
        isNavigationEnabled: true
    });

    const actionManager = useRef(RadarActionManager.getInstance());
    const { t } = useTranslation();

    // S'abonner aux changements d'état
    useEffect(() => {
        const manager = actionManager.current;

        const handleStateUpdate = () => {
            setState(manager.getState());
        };

        manager.on('state:update', handleStateUpdate);
        manager.on('action:executed', handleStateUpdate);

        // Synchroniser l'état initial
        setState(manager.getState());

        return () => {
            manager.off('state:update', handleStateUpdate);
            manager.off('action:executed', handleStateUpdate);
        };
    }, []);

    // Gérer l'animation du canvas
    useEffect(() => {
        if (state.canvas?.animated && !isVisible) {
            // Attendre un peu avant d'afficher pour permettre l'animation CSS
            const timer = setTimeout(() => {
                actionManager.current.executeAction({
                    type: 'startCanvas',
                    params: {}
                });
            }, 100);

            return () => clearTimeout(timer);
        } else {
            const timer = setTimeout(() => {
                actionManager.current.executeAction({
                    type: 'endCanvas',
                    params: {}
                });
            }, 100);

            return () => clearTimeout(timer);
        }
    }, [state.canvas?.animated, isVisible]);

    // Obtenir le scénario actuel
    const getCurrentScenario = () => {
        const id = state.scenario;
        return id && state.scenarios && state.scenarios[id]
            ? state.scenarios[id]
            : null;
    }

    // Convertir les stakeholders pour le radar
    const getRadarData = () => {
        // Filtrer les stakeholders activés
        return Object.values(state.stakeholders || {})
            .filter((s: any) => s.enabled !== false)
            .map((s: any, index: number) => (
                getItemAlteredByScenario({
                    ...StakeholderModelDefaults,
                    ...s,
                    index: index
                }, getCurrentScenario())
            ));
    };

    const getIndicatorsData = () => {
        return Object.values(state.indicators || {})
            //.filter((i: any) => i.enabled)
            .map((i: any, index: number) => (
                getItemAlteredByScenario({
                    ...IndicatorModelDefaults,
                    ...i,
                    index: index
                }, getCurrentScenario())
            ));
    };

    const getRisksData = (isOpportunity: boolean = false) => {
        const risks = Object.values(state.risks || {})
            .filter((r: any) => r.enabled !== false && r.isOpportunity === isOpportunity);

        const risksData = risks.map((r: any, index: number) => (
            getItemAlteredByScenario({
                ...RiskModelDefaults,
                ...r,
                isOpportunity: isOpportunity,
                index: index
            }, getCurrentScenario())
        ));
        return risksData;
    };

    // Classes conditionnelles
    const containerClasses = [
        'radar-display-container',
        isVisible ? 'visible' : '',
        debug ? 'debug-mode' : '',
        className
    ].filter(Boolean).join(' ');

    const likelihoods = [
        { id: '1', name: 'Highly Unlikely' },
        { id: '2', name: 'Unlikely' },
        { id: '3', name: 'Possible' },
        { id: '4', name: 'Likely' },
        { id: '5', name: 'Almost Certain' }
    ]
    const impacts = [
        { id: '1', name: 'Negligible' },
        { id: '2', name: 'Minor' },
        { id: '3', name: 'Moderate' },
        { id: '4', name: 'Major' },
        { id: '5', name: 'Extreme' }
    ]
    const riskCategories = [
        { id: '1', name: 'Existential' },
        { id: '2', name: 'Strategic' },
        { id: '3', name: 'Operational' }
    ]

    const indexedLikelihoods = likelihoods.map((likelihood: any, index: number) => ({
        ...likelihood,
        index: index
    }));

    const indexedImpacts = impacts.map((impact: any, index: number) => ({
        ...impact,
        index: index
    }));

    const indexedRiskCategories = riskCategories.map((riskCategory: any, index: number) => ({
        ...riskCategory,
        index: index
    }));



    return (
        <div id="VisualDisplay" className={containerClasses}>
            <div className="radar-display-content">
                <div className="navigation-container">
                    <ScenarioNavigation
                        scenarios={Object.values(state.scenarios || {})}
                        currentScenario={getCurrentScenario()}
                        onScenarioClick={(scenario) => {
                            actionManager.current.executeAction({
                                type: 'setScenario',
                                params: { to: scenario?.id || null }
                            });
                        }}
                        isNavigationEnabled={state.isNavigationEnabled !== false}
                    />
                    <ScreenNavigation
                        currentScreen={state.currentScreen}
                        onScreenClick={(screen) => {
                            actionManager.current.executeAction({
                                type: 'showScreen',
                                params: { to: screen }
                            });
                        }}
                        isNavigationEnabled={state.isNavigationEnabled !== false}
                    />
                </div>

                {state.currentScreen === 'radar' && isVisible && (
                    <MainRadar
                        scenario={getCurrentScenario()}
                        width={width}
                        height={height}
                        isTransitioning={true}
                        recentCreations={state.recentCreations}
                    >{getRadarData()}</MainRadar>
                )}

                {state.currentScreen === 'indicators' && isVisible && (
                    <>
                        <h2 className="animation-fade-in">{t("models.indicator.name_plural")}</h2>
                        <IndicatorsList
                            indicators={getIndicatorsData()}
                            scenario={getCurrentScenario()}
                            width={width}
                            height={height}
                            readOnly={true}
                            visibleColumns={{
                                id: false,
                                evaluatedAt: false,
                                name: true,
                                status: true,
                                weight: true
                            }}
                        />
                    </>
                )}

                {state.currentScreen === 'risks' && isVisible && (
                    <RiskScatterFullChart
                        onRiskClick={(risk: any) => { }}
                        likelihoods={indexedLikelihoods}
                        impacts={indexedImpacts}
                        style={{
                            margin: 'auto',
                            height: '750px',
                            width: '900px',
                            maxWidth: '800px'
                        }}
                        width={800}
                        height={750}
                        onSelect={() => { }}
                        selection={null}
                        recentCreations={state.recentCreations}
                        risks={getRisksData(false)}
                        opportunities={getRisksData(true)}
                        indicators={getIndicatorsData()}
                        stakeholders={getRadarData()}
                        scenario={getCurrentScenario()}
                        showAllLinks={true}
                        showStakes={true}
                        showRisks={true}
                        showOpportunities={true}
                        showStakeholders={true}
                    />
                )}

                {state.currentScreen === 'synthesis' && isVisible && (
                    <SynthesisGraph
                        scenario={getCurrentScenario()}
                        scenarios={Object.values(state.scenarios || {})}
                        indicators={getIndicatorsData()}
                        stakeholders={getRadarData()}
                        risks={[...getRisksData(false), ...getRisksData(true)]}
                        likelihoods={indexedLikelihoods}
                        impacts={indexedImpacts}
                        features={
                            {
                                indicators: true,
                                constraints: false,
                                risks: true,
                                stakeholders: true,
                                fishbone: false
                            }
                        }
                    />
                )}

                {state.currentScreen === 'image' && state.imageUrl && isVisible && (
                    <div className="radar-image-container">
                        <img src={state.imageUrl} alt="Radar visualization" />
                    </div>
                )}
            </div>
        </div>
    );
}; 